/**
 * Export and import of the app's own Character JSON (docs/design.md, "Character model"). The
 * file is a versioned envelope around one Character, carrying the Snapshot tag so a file from an
 * older Snapshot can be migrated (#17) rather than guessed at.
 */
import type { Character } from "./model";
import { migrateCharacter, MigrationError } from "./migrate";
import { validateCharacter, ValidationError } from "./validate";

export const ENVELOPE_FORMAT = "pathfinder-offline-character" as const;
/** Bump when the envelope itself (not the Character) changes shape. */
export const ENVELOPE_VERSION = 1 as const;
export const EXPORT_MIME = "application/json";

export interface CharacterEnvelope {
  format: typeof ENVELOPE_FORMAT;
  version: typeof ENVELOPE_VERSION;
  /** The Snapshot the Character was saved under. Duplicates `character.snapshotTag` so it is readable without parsing the Character. */
  snapshotTag: string;
  /** ISO timestamp. */
  exportedAt: string;
  /** Version of the app that wrote the file. Informational. */
  app: string;
  character: Character;
}

export function makeEnvelope(character: Character, app: string, exportedAt = new Date().toISOString()): CharacterEnvelope {
  return { format: ENVELOPE_FORMAT, version: ENVELOPE_VERSION, snapshotTag: character.snapshotTag, exportedAt, app, character };
}

export function serializeEnvelope(env: CharacterEnvelope): string {
  return JSON.stringify(env, null, 2) + "\n";
}

/** A file name Safari and Files.app show sensibly: "Valeros.pf2e-character.json". */
export function exportFileName(character: Character): string {
  const stem = character.name.replace(/[^\p{L}\p{N} _-]+/gu, "").trim().replace(/\s+/g, "-") || "character";
  return `${stem}.pf2e-character.json`;
}

export class ImportError extends Error {
  override readonly name = "ImportError";
}

/**
 * Parse an exported file. Throws ImportError with a message fit for the screen. The Character is
 * migrated to the current schema and validated; its id is kept (the store decides on collisions).
 */
export function parseEnvelope(text: string): CharacterEnvelope {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    throw new ImportError(`This is not a JSON file (${(e as Error).message}).`);
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) throw new ImportError("This file does not hold a Character.");
  const o = raw as Record<string, unknown>;
  if (o.format !== ENVELOPE_FORMAT) {
    throw new ImportError(`This is not a Pathfinder Offline Character file (format ${JSON.stringify(o.format ?? "missing")}).`);
  }
  if (o.version !== ENVELOPE_VERSION) {
    throw new ImportError(`This file was written by a newer app (file version ${String(o.version)}, this app reads ${ENVELOPE_VERSION}). Update the app.`);
  }
  if (typeof o.snapshotTag !== "string" || !o.snapshotTag) throw new ImportError("The file names no Snapshot.");
  let character: Character;
  try {
    character = validateCharacter(migrateCharacter(o.character));
  } catch (e) {
    if (e instanceof ValidationError || e instanceof MigrationError) throw new ImportError(`The Character in this file is malformed: ${e.message}.`);
    throw e;
  }
  return {
    format: ENVELOPE_FORMAT,
    version: ENVELOPE_VERSION,
    snapshotTag: o.snapshotTag,
    exportedAt: typeof o.exportedAt === "string" ? o.exportedAt : "",
    app: typeof o.app === "string" ? o.app : "",
    character,
  };
}
